"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/header";
import Footer from "@/components/footer";
import { routing } from "@/i18n/routing";

const EMPTY_FAQ = { question: "", answer: "" };
const MAX_IMAGE_UPLOAD_MB = 15;
const MAX_IMAGE_UPLOAD_BYTES = MAX_IMAGE_UPLOAD_MB * 1024 * 1024;

const BLOCK_DEFAULTS = {
  paragraph: { type: "paragraph", text: "" },
  heading: { type: "heading", level: 2, text: "" },
  list: { type: "list", style: "unordered", items: [""] },
  link: { type: "link", text: "", href: "" },
  quote: { type: "quote", text: "" },
  code: { type: "code", language: "", code: "" },
  image: { type: "image", src: "", alt: "" },
};

function slugifyTitle(title) {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const SITE_URL = "https://highlandgroup.ch";

function toAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function buildAutoSchema(form, slug, faqs) {
  const pageUrl = `${SITE_URL}/${routing.defaultLocale}/blog/${slug || slugifyTitle(form.title) || "your-slug"}`;
  const imageUrl = toAbsoluteUrl(form.featuredImage);
  const validFaqs = faqs.filter((faq) => faq.question.trim() && faq.answer.trim());

  const graph = [
    {
      "@type": "BlogPosting",
      headline: form.metaTitle || form.title || "Blog post title",
      description: form.metaDescription || "",
      url: pageUrl,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": pageUrl,
      },
      author: {
        "@type": "Person",
        name: form.authorName || "Highland Group",
      },
      publisher: {
        "@type": "Organization",
        name: "Highland Group",
        url: SITE_URL,
      },
      datePublished: form.publishedDate || todayISO(),
      dateModified: todayISO(),
      ...(imageUrl
        ? {
            image: {
              "@type": "ImageObject",
              url: imageUrl,
              caption: form.featuredImageAlt || form.title || "",
            },
          }
        : {}),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/${routing.defaultLocale}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${SITE_URL}/${routing.defaultLocale}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: form.title || "Blog Post",
          item: pageUrl,
        },
      ],
    },
  ];

  if (validFaqs.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: validFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question.trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer.trim(),
        },
      })),
    });
  }

  return JSON.stringify(
    {
      "@context": "https://schema.org",
      "@graph": graph,
    },
    null,
    2
  );
}

function normalizeDetectedHref(value = "") {
  const href = String(value).trim();
  if (/^www\./i.test(href)) return `https://${href}`;
  if (/^https?:\/\//i.test(href) || href.startsWith("/") || href.startsWith("#")) return href;
  return "";
}

function cleanInlineText(value = "") {
  return String(value)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function looksLikePlainHeading(line = "", nextLine = "") {
  const text = cleanInlineText(line);
  if (!text || text.length > 110) return false;
  if (/[.!?,;:]$/.test(text)) return false;
  if (/^(https?:\/\/|www\.|[-*+•]\s+|\d+[.)]\s+)/i.test(text)) return false;

  const wordCount = text.split(/\s+/).length;
  if (wordCount > 12) return false;
  if (!nextLine || /^[-*+•]\s+|\d+[.)]\s+/.test(nextLine.trim())) return true;

  const startsLikeTitle = /^[A-Z0-9]/.test(text);
  const titleCaseWords = text
    .split(/\s+/)
    .filter((word) => /^[A-Z0-9]/.test(word.replace(/^[("'`]+/, ""))).length;

  return startsLikeTitle && titleCaseWords >= Math.min(2, wordCount);
}

function preprocessHTMLContent(input = "") {
  return String(input)
    .replace(/<a[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (_, href, text) => {
      const normalizedHref = normalizeDetectedHref(href);
      const label = cleanInlineText(text);
      return normalizedHref && label ? `[${label}](${normalizedHref})` : label;
    })
    .replace(/<(h[1-6])[^>]*>([\s\S]*?)<\/\1>/gi, (_, tag, text) => {
      const level = Math.min(6, Math.max(2, Number(tag.slice(1))));
      return `\n\n${"#".repeat(level)} ${cleanInlineText(text)}\n\n`;
    })
    .replace(/<figure[^>]*>([\s\S]*?)<\/figure>/gi, "\n$1\n")
    .replace(/<img([^>]*)>/gi, (match) => `\n${match}\n`)
    .replace(/<\/p\s*>/gi, "\n\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
      let n = 0;
      return "\n" + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, text) => {
        n++;
        return `\n${n}. ${text.replace(/<[^>]+>/g, "").trim()}`;
      }) + "\n";
    })
    .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) =>
      "\n" + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, text) =>
        `\n- ${text.replace(/<[^>]+>/g, "").trim()}`
      ) + "\n"
    )
    .replace(/<li[^>]*>/gi, "\n- ")
    .replace(/<\/li\s*>/gi, "")
    .replace(/<\/?(?:ul|ol|div|section|article|blockquote|main|header|footer|span|strong|b|em|i)[^>]*>/gi, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function parseContentToBlocks(input = "") {
  const blocks = [];
  const paragraphLines = [];
  let listStyle = null;
  let listItems = [];
  let codeBuffer = null;

  const flushParagraph = () => {
    const text = cleanInlineText(paragraphLines.join(" "));
    if (text) blocks.push({ type: "paragraph", text });
    paragraphLines.length = 0;
  };

  const flushList = () => {
    if (listItems.length) {
      blocks.push({ type: "list", style: listStyle || "unordered", items: listItems });
    }
    listStyle = null;
    listItems = [];
  };
  const flushCode = () => {
    if (codeBuffer && codeBuffer.lines.length) {
      blocks.push({
        type: "code",
        language: codeBuffer.language,
        code: codeBuffer.lines.join("\n"),
      });
    }
    codeBuffer = null;
  };

  const preprocessed = preprocessHTMLContent(input);
  const lines = preprocessed.replace(/\r\n/g, "\n").split("\n");

  for (let index = 0; index < lines.length; index++) {
    const rawLine = lines[index];
    const line = rawLine.trim();
    const nextLine = lines.slice(index + 1).find((item) => item.trim())?.trim() || "";

    const codeFence = line.match(/^```([a-z0-9_-]*)\s*$/i);
    if (codeFence) {
      if (codeBuffer) {
        flushCode();
      } else {
        flushParagraph();
        flushList();
        codeBuffer = { language: codeFence[1] || "", lines: [] };
      }
      continue;
    }

    if (codeBuffer) {
      codeBuffer.lines.push(rawLine);
      continue;
    }

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const htmlHeading = line.match(/^<h([2-6])[^>]*>(.*?)<\/h\1>$/i);
    const markdownHeading = line.match(/^(#{2,6})\s+(.+)$/);
    if (htmlHeading || markdownHeading) {
      flushParagraph();
      flushList();
      const level = htmlHeading ? Number(htmlHeading[1]) : markdownHeading[1].length;
      const text = cleanInlineText(htmlHeading ? htmlHeading[2] : markdownHeading[2]);
      if (text) blocks.push({ type: "heading", level, text });
      continue;
    }

    const markdownImage = line.match(/^!\[(.*?)\]\((.*?)\)$/);
    const htmlImage = line.match(/^<img[^>]*src=["']([^"']+)["'][^>]*>/i);
    if (markdownImage || htmlImage) {
      flushParagraph();
      flushList();
      const src = markdownImage ? markdownImage[2].trim() : htmlImage[1].trim();
      const altMatch = line.match(/alt=["']([^"']*)["']/i);
      const alt = cleanInlineText(markdownImage ? markdownImage[1] : altMatch?.[1] || "");
      if (src) blocks.push({ type: "image", src, alt });
      continue;
    }

    const standaloneMarkdownLink = line.match(/^\[([^\]]+)\]\(((?:https?:\/\/|www\.|\/|#)[^\s)]+)\)$/i);
    const standaloneUrl = line.match(/^(?:https?:\/\/|www\.)[^\s<>]+$/i);
    if (standaloneMarkdownLink || standaloneUrl) {
      flushParagraph();
      flushList();
      const href = normalizeDetectedHref(standaloneMarkdownLink?.[2] || standaloneUrl?.[0]);
      const text = cleanInlineText(standaloneMarkdownLink?.[1] || standaloneUrl?.[0]);
      if (href && text) blocks.push({ type: "link", text, href });
      continue;
    }

    const quoteMatch = line.match(/^>\s+(.+)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();
      blocks.push({ type: "quote", text: cleanInlineText(quoteMatch[1]) });
      continue;
    }

    const unorderedItem = line.match(/^[-*+•]\s+(.+)$/);
    const orderedItem = line.match(/^\d+[.)]\s+(.+)$/);
    if (unorderedItem || orderedItem) {
      flushParagraph();
      const nextStyle = orderedItem ? "ordered" : "unordered";
      if (listStyle && listStyle !== nextStyle) flushList();
      listStyle = nextStyle;
      listItems.push(cleanInlineText(unorderedItem ? unorderedItem[1] : orderedItem[1]));
      continue;
    }

    if (!paragraphLines.length && looksLikePlainHeading(line, nextLine)) {
      flushList();
      blocks.push({ type: "heading", level: 2, text: cleanInlineText(line) });
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  flushCode();

  return blocks;
}

function blocksToEditorText(blocks = []) {
  return blocks
    .map((block) => {
      if (block.type === "paragraph") return block.text || "";
      if (block.type === "heading") return `${"#".repeat(block.level || 2)} ${block.text || ""}`;
      if (block.type === "list") {
        return (block.items || [])
          .map((item, index) => block.style === "ordered" ? `${index + 1}. ${item}` : `- ${item}`)
          .join("\n");
      }
      if (block.type === "link") return `[${block.text || block.href || "link"}](${block.href || ""})`;
      if (block.type === "quote") return `> ${block.text || ""}`;
      if (block.type === "code") return `\`\`\`${block.language || ""}\n${block.code || ""}\n\`\`\``;
      if (block.type === "image") return `![${block.alt || ""}](${block.src || ""})`;
      return "";
    })
    .filter(Boolean)
    .join("\n\n");
}

function renderEditorPreviewBlock(block, index) {
  if (block.type === "heading") {
    const Tag = `h${Math.min(6, Math.max(2, block.level || 2))}`;
    return <Tag key={index} className="font-bold text-charcoal mt-4 mb-2">{block.text}</Tag>;
  }
  if (block.type === "list") {
    const Tag = block.style === "ordered" ? "ol" : "ul";
    return (
      <Tag key={index} className={`${block.style === "ordered" ? "list-decimal" : "list-disc"} pl-5 text-sm text-muted space-y-1`}>
        {(block.items || []).map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}
      </Tag>
    );
  }
  if (block.type === "link") {
    return <p key={index}><a href={block.href} className="text-accent-red font-semibold underline underline-offset-2">{block.text || block.href}</a></p>;
  }
  if (block.type === "quote") {
    return <blockquote key={index} className="border-l-4 border-accent-red/30 pl-4 italic text-charcoal">{block.text}</blockquote>;
  }
  if (block.type === "code") {
    return <pre key={index} className="overflow-auto rounded-xl bg-charcoal text-white text-xs p-4"><code>{block.code}</code></pre>;
  }
  if (block.type === "image") {
    return <img key={index} src={block.src} alt={block.alt || ""} className="max-h-[220px] rounded-xl border border-border-light object-cover" />;
  }
  return <p key={index} className="text-sm leading-6 text-muted">{block.text}</p>;
}

const INITIAL_FORM = {
  title: "",
  metaTitle: "",
  metaDescription: "",
  keywords: "",
  schema: "",
  slug: "",
  authorName: "",
  publishedDate: todayISO(),
  featuredImage: "",
  featuredImageAlt: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
};

const INITIAL_OG_TOUCHED = { title: false, description: false, image: false };

const INPUT_CLASS =
  "w-full bg-bg-soft border border-border-light rounded-xl text-charcoal text-sm px-4 py-2.5 outline-none focus:ring-4 focus:ring-accent-red/15 transition-all placeholder:text-muted";
const LABEL_CLASS = "block text-xs font-bold uppercase tracking-wide text-charcoal mb-1.5";
const HINT_CLASS = "text-xs text-muted mt-1.5";
const CARD_CLASS =
  "bg-bg-main border border-border-light rounded-2xl p-6 sm:p-8 mb-6 shadow-[0_18px_42px_rgba(217,31,38,0.06)]";
const CARD_TITLE_CLASS =
  "flex items-center gap-2.5 text-base font-semibold text-charcoal mb-5 pb-3.5 border-b border-border-light";
const CARD_TITLE_ICON_CLASS =
  "w-7 h-7 rounded-md bg-accent-red/10 inline-flex items-center justify-center text-sm";
const BTN_BASE_CLASS =
  "text-sm font-bold rounded-lg transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-45 disabled:cursor-not-allowed";
const BTN_PRIMARY_CLASS = `${BTN_BASE_CLASS} bg-accent-red text-white px-8 py-3 text-sm shadow-lg shadow-accent-red/30 hover:bg-red-700`;
const BTN_SECONDARY_CLASS = `${BTN_BASE_CLASS} bg-bg-main border border-border-light text-charcoal px-4 py-2`;
const BTN_DANGER_CLASS = `${BTN_BASE_CLASS} bg-accent-red/5 border border-accent-red/20 text-accent-red px-4 py-2`;
const BTN_GHOST_CLASS = `${BTN_BASE_CLASS} bg-accent-red/8 border border-accent-red/20 text-accent-red px-3.5 py-1.5 text-xs`;
const BTN_ADD_BLOCK_CLASS = `${BTN_BASE_CLASS} bg-accent-red/8 border border-dashed border-accent-red/30 text-accent-red text-xs px-3.5 py-1.5 hover:bg-accent-red/15`;
const BTN_REMOVE_INLINE_CLASS = `${BTN_BASE_CLASS} bg-transparent border border-accent-red/20 text-accent-red text-xs px-3 py-1.5 whitespace-nowrap`;
const BTN_TEXT_LINK_CLASS =
  "bg-transparent border-0 text-accent-red text-sm font-semibold underline underline-offset-2 p-0 cursor-pointer disabled:opacity-45";
const ITEM_CLASS = "bg-bg-soft border border-border-light rounded-xl p-5 mb-3.5";
const ITEM_HEADER_CLASS = "flex items-center justify-between flex-wrap gap-2.5 mb-3.5";
const TYPE_BADGE_CLASS =
  "text-[11px] font-bold uppercase tracking-wide text-accent-red bg-accent-red/10 px-2.5 py-0.5 rounded-full";

export default function CreateBlogAdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [blogList, setBlogList] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const [uploadingBlockIndex, setUploadingBlockIndex] = useState(null);
  const [alert, setAlert] = useState(null);

  const [form, setForm] = useState({ ...INITIAL_FORM, publishedDate: todayISO() });
  const [ogTouched, setOgTouched] = useState({ ...INITIAL_OG_TOUCHED });
  const [schemaManuallyEdited, setSchemaManuallyEdited] = useState(false);
  const [faqs, setFaqs] = useState([{ ...EMPTY_FAQ }]);
  const faqAnswerRefs = useRef({});
  const faqSelectionRefs = useRef({});
  const contentEditorRef = useRef(null);
  const [contentBlocks, setContentBlocks] = useState([{ type: "paragraph", text: "" }]);
  const [contentImport, setContentImport] = useState("");
  const [contentMode, setContentMode] = useState("write");

  const slugPreview = useMemo(() => form.slug || slugifyTitle(form.title), [form.slug, form.title]);
  const autoSchema = useMemo(
    () => buildAutoSchema(form, slugPreview, faqs),
    [form, slugPreview, faqs]
  );
  const contentWordCount = useMemo(() => {
    return cleanInlineText(contentImport || blocksToEditorText(contentBlocks))
      .split(/\s+/)
      .filter(Boolean).length;
  }, [contentBlocks, contentImport]);
  const contentPreviewBlocks = useMemo(
    () => parseContentToBlocks(contentImport || blocksToEditorText(contentBlocks)),
    [contentBlocks, contentImport]
  );

  const fetchBlogList = useCallback(async () => {
    setLoadingBlogs(true);
    try {
      const response = await fetch("/api/admin/blogs");
      const result = await response.json();
      if (response.status === 401) { navigate("/admin/login"); return; }
      if (response.ok && result.success) setBlogList(result.blogs || []);
    } catch (e) {
      console.error("Failed to load blogs:", e);
    } finally {
      setLoadingBlogs(false);
    }
  }, [navigate]);

  useEffect(() => { fetchBlogList(); }, [fetchBlogList]);

  useEffect(() => {
    if (!schemaManuallyEdited) {
      setForm((prev) => ({ ...prev, schema: autoSchema }));
    }
  }, [autoSchema, schemaManuallyEdited]);

  function resetForm() {
    setForm({ ...INITIAL_FORM, publishedDate: todayISO() });
    setOgTouched({ ...INITIAL_OG_TOUCHED });
    setSchemaManuallyEdited(false);
    setFaqs([{ ...EMPTY_FAQ }]);
    setContentBlocks([{ type: "paragraph", text: "" }]);
    setContentImport("");
    setContentMode("write");
    setEditingSlug(null);
  }

  function populateFormFromBlog(blog) {
    setForm({
      title: blog.title || "",
      metaTitle: blog.metaTitle || "",
      metaDescription: blog.metaDescription || "",
      keywords: blog.keywords || "",
      schema: blog.schema ? JSON.stringify(blog.schema, null, 2) : "",
      slug: blog.slug || "",
      authorName: blog.authorName || "",
      publishedDate: blog.publishedDate || todayISO(),
      featuredImage: blog.featuredImage || "",
      featuredImageAlt: blog.featuredImageAlt || "",
      ogTitle: blog.ogTitle || blog.metaTitle || "",
      ogDescription: blog.ogDescription || blog.metaDescription || "",
      ogImage: blog.ogImage || blog.featuredImage || "",
    });
    setOgTouched({
      title: Boolean(blog.ogTitle),
      description: Boolean(blog.ogDescription),
      image: Boolean(blog.ogImage),
    });
    setSchemaManuallyEdited(Boolean(blog.schema));
    setFaqs(blog.faqs?.length ? blog.faqs : [{ ...EMPTY_FAQ }]);
    setContentBlocks(blog.content?.length ? blog.content : [{ type: "paragraph", text: "" }]);
    setContentImport(blocksToEditorText(blog.content?.length ? blog.content : [{ type: "paragraph", text: "" }]));
    setContentMode("write");
    setEditingSlug(blog.slug);
  }

  async function startEdit(slug) {
    setLoadingEdit(true);
    setAlert(null);
    try {
      const res = await fetch(`/api/admin/blogs/${encodeURIComponent(slug)}`);
      const result = await res.json();
      if (res.status === 401) { navigate("/admin/login"); return; }
      if (!res.ok || !result.success) { setAlert({ type: "error", message: result.error || "Failed to load blog." }); return; }
      populateFormFromBlog(result.blog);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to load blog." });
    } finally {
      setLoadingEdit(false);
    }
  }

  async function handleDelete(slug) {
    const blog = blogList.find((b) => b.slug === slug);
    if (!window.confirm(`Delete "${blog?.title || slug}"? This cannot be undone.`)) return;
    setDeletingSlug(slug);
    setAlert(null);
    try {
      const res = await fetch(`/api/admin/blogs/${encodeURIComponent(slug)}`, { method: "DELETE" });
      const result = await res.json();
      if (res.status === 401) { navigate("/admin/login"); return; }
      if (!res.ok || !result.success) { setAlert({ type: "error", message: result.error || "Failed to delete." }); return; }
      if (editingSlug === slug) resetForm();
      setAlert({ type: "success", message: "Blog deleted successfully." });
      await fetchBlogList();
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to delete." });
    } finally {
      setDeletingSlug(null);
    }
  }

  function updateForm(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "title" && !editingSlug && !prev.slug) next.slug = slugifyTitle(value);
      if (field === "metaTitle" && !ogTouched.title) next.ogTitle = value;
      if (field === "metaDescription" && !ogTouched.description) next.ogDescription = value;
      if (field === "featuredImage" && !ogTouched.image) next.ogImage = value;
      return next;
    });
  }

  function updateOgField(touchedKey, field, value) {
    setOgTouched((prev) => ({ ...prev, [touchedKey]: true }));
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function generateSchema() {
    setForm((prev) => ({ ...prev, schema: autoSchema }));
    setSchemaManuallyEdited(false);
    setAlert({ type: "success", message: "Schema generated from the current blog fields." });
  }

  function addFaq() { setFaqs((p) => [...p, { ...EMPTY_FAQ }]); }
  function removeFaq(i) { setFaqs((p) => p.filter((_, idx) => idx !== i)); }
  function updateFaq(i, field, value) { setFaqs((p) => p.map((f, idx) => idx === i ? { ...f, [field]: value } : f)); }

  function rememberFaqSelection(index) {
    const el = faqAnswerRefs.current[index];
    if (el) faqSelectionRefs.current[index] = { start: el.selectionStart, end: el.selectionEnd };
  }

  function wrapFaqAnswerSelection(index, prefix, suffix, placeholder) {
    const value = faqs[index]?.answer || "";
    const sel = faqSelectionRefs.current[index] || { start: value.length, end: value.length };
    const selected = value.slice(sel.start, sel.end) || placeholder;
    const newValue = value.slice(0, sel.start) + prefix + selected + suffix + value.slice(sel.end);
    const newStart = sel.start + prefix.length;
    const newEnd = newStart + selected.length;

    updateFaq(index, "answer", newValue);
    faqSelectionRefs.current[index] = { start: newStart, end: newEnd };
    requestAnimationFrame(() => {
      const el = faqAnswerRefs.current[index];
      if (el) {
        el.focus();
        el.setSelectionRange(newStart, newEnd);
      }
    });
  }

  function insertFaqAnswerLink(index) {
    const value = faqs[index]?.answer || "";
    const sel = faqSelectionRefs.current[index] || { start: value.length, end: value.length };
    const selected = value.slice(sel.start, sel.end) || "link text";
    const url = window.prompt("Link URL", "https://");
    const href = normalizeDetectedHref(url);
    if (!href) return;

    const syntax = `[${selected}](${href})`;
    const newValue = value.slice(0, sel.start) + syntax + value.slice(sel.end);
    const newStart = sel.start + 1;
    const newEnd = newStart + selected.length;

    updateFaq(index, "answer", newValue);
    faqSelectionRefs.current[index] = { start: newStart, end: newEnd };
    requestAnimationFrame(() => {
      const el = faqAnswerRefs.current[index];
      if (el) {
        el.focus();
        el.setSelectionRange(newStart, newEnd);
      }
    });
  }

  function addContentBlock(type) {
    const t = BLOCK_DEFAULTS[type] || BLOCK_DEFAULTS.paragraph;
    setContentBlocks((p) => [...p, JSON.parse(JSON.stringify(t))]);
  }
  function syncEditorText(value) {
    setContentImport(value);
  }
  function replaceContentSelection(nextValue, selectionStart, selectionEnd) {
    syncEditorText(nextValue);
    requestAnimationFrame(() => {
      const el = contentEditorRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }
  function wrapContentSelection(prefix, suffix = "", placeholder = "text") {
    const el = contentEditorRef.current;
    const value = contentImport;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || placeholder;
    const nextValue = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    replaceContentSelection(nextValue, start + prefix.length, start + prefix.length + selected.length);
  }
  function insertContentLine(snippet) {
    const el = contentEditorRef.current;
    const value = contentImport;
    const start = el?.selectionStart ?? value.length;
    const prefix = start > 0 && value[start - 1] !== "\n" ? "\n" : "";
    const nextValue = value.slice(0, start) + prefix + snippet + value.slice(start);
    const cursor = start + prefix.length + snippet.length;
    replaceContentSelection(nextValue, cursor, cursor);
  }
  function insertContentLink() {
    const el = contentEditorRef.current;
    const value = contentImport;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || "link text";
    const url = window.prompt("Link URL", "https://");
    const href = normalizeDetectedHref(url);
    if (!href) return;
    const syntax = `[${selected}](${href})`;
    const nextValue = value.slice(0, start) + syntax + value.slice(end);
    replaceContentSelection(nextValue, start + 1, start + 1 + selected.length);
  }
  function importContentBlocks() {
    const blocks = parseContentToBlocks(contentImport);
    if (!blocks.length) {
      setAlert({ type: "error", message: "No headings, paragraphs, lists, or images were found in the pasted content." });
      return;
    }
    setContentBlocks(blocks);
    setAlert({ type: "success", message: `Created ${blocks.length} content blocks from pasted content.` });
  }
  function handleContentImportPaste(e) {
    const html = e.clipboardData?.getData("text/html") || "";
    const text = e.clipboardData?.getData("text/plain") || "";
    const source = html || text;
    if (!source) return;

    const currentValue = contentImport;
    const start = e.currentTarget.selectionStart ?? 0;
    const end = e.currentTarget.selectionEnd ?? 0;
    const isReplacingAll = start === 0 && end === currentValue.length;
    const isEmptyEditor = !currentValue.trim();

    if (!isEmptyEditor && !isReplacingAll) {
      return;
    }

    const blocks = parseContentToBlocks(source);
    if (!blocks.length) return;

    e.preventDefault();
    setContentImport(text || cleanInlineText(html));
    setContentBlocks(blocks);
    setAlert({ type: "success", message: `Auto picked ${blocks.length} content blocks from pasted content.` });
  }
  function removeContentBlock(i) { setContentBlocks((p) => p.filter((_, idx) => idx !== i)); }
  function updateContentBlock(i, updates) { setContentBlocks((p) => p.map((b, idx) => idx === i ? { ...b, ...updates } : b)); }
  function updateListItem(bi, ii, val) {
    setContentBlocks((p) => p.map((b, i) => {
      if (i !== bi || b.type !== "list") return b;
      const items = [...b.items]; items[ii] = val; return { ...b, items };
    }));
  }
  function addListItem(bi) { setContentBlocks((p) => p.map((b, i) => i !== bi || b.type !== "list" ? b : { ...b, items: [...b.items, ""] })); }
  function removeListItem(bi, ii) {
    setContentBlocks((p) => p.map((b, i) => {
      if (i !== bi || b.type !== "list") return b;
      const items = b.items.filter((_, idx) => idx !== ii);
      return { ...b, items: items.length ? items : [""] };
    }));
  }
  function moveBlock(i, dir) {
    setContentBlocks((p) => {
      const next = [...p]; const t = i + dir;
      if (t < 0 || t >= next.length) return p;
      [next[i], next[t]] = [next[t], next[i]]; return next;
    });
  }

  async function uploadImageFile(file) {
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      throw new Error(`Image must be ${MAX_IMAGE_UPLOAD_MB} MB or smaller.`);
    }
    const fd = new FormData(); fd.append("file", file);
    const res = await fetch("/api/admin/upload-image", { method: "POST", body: fd });
    // Read as text first: a proxy/nginx error page is HTML, and res.json() would throw a confusing parse error.
    const raw = await res.text();
    let result = {};
    try { result = JSON.parse(raw); } catch { result = {}; }
    if (res.status === 401) { navigate("/admin/login"); return null; }
    if (!res.ok || !result.success) {
      if (res.status === 413) throw new Error(`Image is too large for the server (limit ${MAX_IMAGE_UPLOAD_MB} MB). Check the proxy/nginx upload limit.`);
      throw new Error(result.error || `Upload failed (HTTP ${res.status}).`);
    }
    return result.path;
  }

  async function handleFeaturedImageUpload(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingFeatured(true); setAlert(null);
    try { const path = await uploadImageFile(file); if (path) updateForm("featuredImage", path); }
    catch (err) { setAlert({ type: "error", message: err.message || "Upload failed." }); }
    finally { setUploadingFeatured(false); }
  }

  async function handleOgImageUpload(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingOgImage(true); setAlert(null);
    try { const path = await uploadImageFile(file); if (path) updateOgField("image", "ogImage", path); }
    catch (err) { setAlert({ type: "error", message: err.message || "Upload failed." }); }
    finally { setUploadingOgImage(false); }
  }

  async function handleContentBlockImageUpload(blockIndex, e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingBlockIndex(blockIndex); setAlert(null);
    try { const path = await uploadImageFile(file); if (path) updateContentBlock(blockIndex, { src: path }); }
    catch (err) { setAlert({ type: "error", message: err.message || "Upload failed." }); }
    finally { setUploadingBlockIndex(null); }
  }

  async function handleContentEditorImageUpload(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingBlockIndex("editor"); setAlert(null);
    try {
      const path = await uploadImageFile(file);
      if (path) insertContentLine(`![${file.name.replace(/\.[^.]+$/, "")}](${path})`);
    } catch (err) {
      setAlert({ type: "error", message: err.message || "Upload failed." });
    } finally {
      setUploadingBlockIndex(null);
    }
  }

  async function handleLogout() {
    setLoggingOut(true);
    try { await fetch("/api/admin/logout", { method: "POST" }); navigate("/admin/login"); }
    finally { setLoggingOut(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault(); setLoading(true); setAlert(null);
    let schema = null;

    if (form.schema.trim()) {
      try {
        schema = JSON.parse(form.schema);
      } catch {
        setAlert({ type: "error", message: "Schema must be valid JSON-LD." });
        setLoading(false);
        return;
      }
    }

    const parsedEditorBlocks = parseContentToBlocks(contentImport);
    const payload = {
      ...form,
      slug: form.slug || slugifyTitle(form.title),
      schema,
      faqs: faqs.filter((f) => f.question.trim() && f.answer.trim()),
      content: parsedEditorBlocks.length ? parsedEditorBlocks : contentBlocks,
    };
    try {
      const isEditing = Boolean(editingSlug);
      const res = await fetch(
        isEditing ? `/api/admin/blogs/${encodeURIComponent(editingSlug)}` : "/api/create-blog",
        { method: isEditing ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
      );
      const result = await res.json();
      if (res.status === 401) { navigate("/admin/login"); return; }
      if (!res.ok || !result.success) {
        if (res.status === 409 && result.suggestedSlug) {
          setForm((c) => ({ ...c, slug: result.suggestedSlug }));
          setAlert({ type: "error", message: `${result.error} Suggested slug: ${result.suggestedSlug} — filled in for you.` });
        } else {
          setAlert({ type: "error", message: result.error || (isEditing ? "Failed to update." : "Failed to create.") });
        }
        return;
      }
      setAlert({ type: "success", message: isEditing ? `Blog updated at ${result.url}` : `Blog created at ${result.url}` });
      if (isEditing) setEditingSlug(result.slug || payload.slug);
      else resetForm();
      await fetchBlogList();
    } catch (err) {
      setAlert({ type: "error", message: err.message || "Network error." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-bg-soft pt-28 pb-20">
      <div className="container mx-auto px-4">

        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-3.5">
            <span className="bg-accent-red/10 text-accent-red text-[10px] font-extrabold uppercase tracking-widest px-3.5 py-1 rounded-full">
              Admin
            </span>
            <h1 className="text-2xl sm:text-3xl font-semibold text-charcoal m-0">
              {editingSlug ? "Edit Blog Post" : "Create Blog Post"}
            </h1>
          </div>
          <button
            className="bg-bg-main border border-border-light text-muted text-sm font-semibold px-5 py-2 rounded-full transition-colors hover:border-accent-red hover:text-accent-red disabled:opacity-50"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? "Logging out…" : "Logout"}
          </button>
        </div>

        {/* Alert */}
        {alert && (
          <div
            className={`rounded-xl px-5 py-3.5 text-sm font-medium mb-7 border ${
              alert.type === "success"
                ? "bg-green-50 border-green-200 text-green-700"
                : "bg-accent-red/5 border-accent-red/20 text-accent-red"
            }`}
          >
            {alert.message}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* SEO & Basics */}
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <span className={CARD_TITLE_ICON_CLASS}>🔍</span> SEO &amp; Basics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>Title *</label>
                <input required className={INPUT_CLASS} value={form.title}
                  onChange={(e) => updateForm("title", e.target.value)}
                  placeholder="Your blog post title" />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>Meta Title *</label>
                <input required className={INPUT_CLASS} value={form.metaTitle}
                  onChange={(e) => updateForm("metaTitle", e.target.value)}
                  placeholder="SEO title (shown in search results)" />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>Meta Description *</label>
                <textarea required rows={3} className={`${INPUT_CLASS} resize-y min-h-[90px]`} value={form.metaDescription}
                  onChange={(e) => updateForm("metaDescription", e.target.value)}
                  placeholder="Brief description for search engines (150–160 chars)" />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>Keywords</label>
                <input className={INPUT_CLASS} value={form.keywords}
                  onChange={(e) => updateForm("keywords", e.target.value)}
                  placeholder="keyword one, keyword two, keyword three" />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className={`${LABEL_CLASS} mb-0`}>Schema JSON-LD</label>
                  <button type="button" className={BTN_ADD_BLOCK_CLASS} onClick={generateSchema}>
                    Auto Generate Schema
                  </button>
                </div>
                <textarea rows={8} className={`${INPUT_CLASS} resize-y min-h-[90px] mt-2 font-mono`} value={form.schema}
                  onChange={(e) => {
                    setSchemaManuallyEdited(true);
                    updateForm("schema", e.target.value);
                  }}
                  placeholder='{"@context":"https://schema.org","@type":"Article","headline":"Your blog title"}' />
                <p className={HINT_CLASS}>Auto-generated from title, meta, URL, author, image, and FAQs. You can edit it manually.</p>
              </div>
              <div>
                <label className={LABEL_CLASS}>Slug * (auto-generated from title)</label>
                <input required className={INPUT_CLASS} value={form.slug}
                  onChange={(e) => updateForm("slug", slugifyTitle(e.target.value))}
                  placeholder="my-blog-post" />
                <p className={HINT_CLASS}>URL: /{routing.defaultLocale}/blog/<span className="text-accent-red">{slugPreview || "your-slug"}</span></p>
              </div>
            </div>
          </div>

          {/* Author & Dates */}
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <span className={CARD_TITLE_ICON_CLASS}>👤</span> Author &amp; Dates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLASS}>Author Name *</label>
                <input required className={INPUT_CLASS} value={form.authorName}
                  onChange={(e) => updateForm("authorName", e.target.value)}
                  placeholder="Jane Doe" />
              </div>
              <div>
                <label className={LABEL_CLASS}>Published Date *</label>
                <input required type="date" className={INPUT_CLASS} value={form.publishedDate}
                  onChange={(e) => updateForm("publishedDate", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <span className={CARD_TITLE_ICON_CLASS}>🖼️</span> Featured Image
            </h2>
            <div className="space-y-3">
              <div>
                <label className={LABEL_CLASS}>Image File</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 bg-accent-red/8 border border-accent-red/20 text-accent-red text-sm font-semibold px-4.5 py-2 rounded-lg cursor-pointer transition-colors hover:bg-accent-red/15">
                    {uploadingFeatured ? "Uploading..." : "Upload Image"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFeaturedImageUpload} disabled={uploadingFeatured}
                      className="hidden" />
                  </label>
                  <span className="text-[11px] text-muted">JPEG, PNG, WebP, GIF - max {MAX_IMAGE_UPLOAD_MB} MB</span>
                </div>
                <div className="mt-3">
                  <input required className={INPUT_CLASS} value={form.featuredImage}
                    onChange={(e) => updateForm("featuredImage", e.target.value)}
                    placeholder="/images/blog/my-post.jpg" />
                </div>
                {form.featuredImage && (
                  <img src={form.featuredImage} alt={form.featuredImageAlt || "Preview"}
                    className="mt-3.5 max-h-[180px] rounded-xl border border-border-light object-cover block" />
                )}
              </div>
              <div>
                <label className={LABEL_CLASS}>Image Alt Text *</label>
                <input required className={INPUT_CLASS} value={form.featuredImageAlt}
                  onChange={(e) => updateForm("featuredImageAlt", e.target.value)}
                  placeholder="Describe the image for accessibility" />
              </div>
            </div>
          </div>

          {/* Open Graph / Social Sharing */}
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <span className={CARD_TITLE_ICON_CLASS}>🔗</span> Open Graph &amp; Social Sharing
            </h2>
            <p className={`${HINT_CLASS} -mt-2 mb-4`}>
              Auto-filled from Meta Title, Meta Description, and Featured Image. Edit any field below to override.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>OG Title</label>
                <input className={INPUT_CLASS} value={form.ogTitle}
                  onChange={(e) => updateOgField("title", "ogTitle", e.target.value)}
                  placeholder="Title shown when shared on social media" />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>OG Description</label>
                <textarea rows={3} className={`${INPUT_CLASS} resize-y min-h-[90px]`} value={form.ogDescription}
                  onChange={(e) => updateOgField("description", "ogDescription", e.target.value)}
                  placeholder="Description shown when shared on social media" />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL_CLASS}>OG Image</label>
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <label className="inline-flex items-center gap-2 bg-accent-red/8 border border-accent-red/20 text-accent-red text-sm font-semibold px-4.5 py-2 rounded-lg cursor-pointer transition-colors hover:bg-accent-red/15">
                    {uploadingOgImage ? "Uploading..." : "Upload Image"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleOgImageUpload} disabled={uploadingOgImage}
                      className="hidden" />
                  </label>
                  <span className="text-[11px] text-muted">JPEG, PNG, WebP, GIF - max {MAX_IMAGE_UPLOAD_MB} MB</span>
                </div>
                <input className={INPUT_CLASS} value={form.ogImage}
                  onChange={(e) => updateOgField("image", "ogImage", e.target.value)}
                  placeholder="/images/blog/my-post.jpg" />
                {form.ogImage && (
                  <img src={form.ogImage} alt="OG preview"
                    className="mt-3.5 max-h-[180px] rounded-xl border border-border-light object-cover block" />
                )}
              </div>
            </div>
          </div>

          {/* Content Blocks */}
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <span className={CARD_TITLE_ICON_CLASS}>📝</span> Content Blocks
            </h2>
            <div className={ITEM_CLASS}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="inline-flex rounded-xl border border-border-light bg-bg-soft p-1">
                  {["write", "preview"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`${BTN_BASE_CLASS} px-4 py-2 text-xs ${contentMode === mode ? "bg-accent-red text-white" : "text-muted"}`}
                      onClick={() => setContentMode(mode)}
                    >
                      {mode === "write" ? "Write" : "Preview"}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-muted">
                  <span>{contentWordCount} words</span>
                  <span>{contentPreviewBlocks.length} blocks detected</span>
                </div>
              </div>

              {contentMode === "write" ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button type="button" className={BTN_GHOST_CLASS} onClick={() => wrapContentSelection("**", "**", "bold text")}>Bold</button>
                    {[2, 3, 4, 5, 6].map((level) => (
                      <button key={level} type="button" className={BTN_GHOST_CLASS} onClick={() => insertContentLine(`${"#".repeat(level)} Heading ${level}\n`)}>
                        H{level}
                      </button>
                    ))}
                    <button type="button" className={BTN_GHOST_CLASS} onClick={() => insertContentLine("- List item\n")}>Bullet</button>
                    <button type="button" className={BTN_GHOST_CLASS} onClick={() => insertContentLine("1. Numbered item\n")}>1. Numbered</button>
                    <button type="button" className={BTN_GHOST_CLASS} onClick={() => insertContentLine("> Quote text\n")}>Quote</button>
                    <button type="button" className={BTN_GHOST_CLASS} onClick={() => insertContentLine("```js\n// code\n```\n")}>{"{ } Code"}</button>
                    <button type="button" className={BTN_GHOST_CLASS} onClick={insertContentLink}>Link</button>
                    <label className={`${BTN_GHOST_CLASS} cursor-pointer`}>
                      {uploadingBlockIndex === "editor" ? "Uploading..." : "Upload image"}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleContentEditorImageUpload}
                        disabled={uploadingBlockIndex === "editor"}
                        className="hidden" />
                    </label>
                    <button type="button" className={BTN_ADD_BLOCK_CLASS} onClick={importContentBlocks}>
                      Generate Blocks
                    </button>
                  </div>
                  <textarea
                    ref={contentEditorRef}
                    rows={12}
                    className={`${INPUT_CLASS} resize-y min-h-[260px] font-mono leading-6`}
                    value={contentImport}
                    onChange={(e) => syncEditorText(e.target.value)}
                    onPaste={handleContentImportPaste}
                    placeholder={"Paste full content here from WordPress, Google Docs, or any article. Use the toolbar for Bold, H2-H6, bullets, numbered lists, quotes, code, links, and images."}
                  />
                  <p className={HINT_CLASS}>Paste content here to auto detect blocks immediately, or edit text and click Generate Blocks.</p>
                </>
              ) : (
                <div className={`${ITEM_CLASS} min-h-[260px] space-y-4 bg-white`}>
                  {contentPreviewBlocks.length ? (
                    contentPreviewBlocks.map((block, index) => renderEditorPreviewBlock(block, index))
                  ) : (
                    <p className="text-sm text-muted">Nothing to preview yet.</p>
                  )}
                </div>
              )}

              <details className="mt-5">
                <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-accent-red">
                  Advanced block editor
                </summary>
              <div className={ITEM_HEADER_CLASS}>
                <span className={TYPE_BADGE_CLASS}>Auto pick content</span>
                <button type="button" className={BTN_ADD_BLOCK_CLASS} onClick={importContentBlocks}>
                  Generate Blocks
                </button>
              </div>
              <textarea rows={7} className={`${INPUT_CLASS} resize-y min-h-[90px]`} value={contentImport}
                onChange={(e) => setContentImport(e.target.value)}
                onPaste={handleContentImportPaste}
                placeholder={"Paste full content here from WordPress, Google Docs, or any article. Headings, paragraphs, bullets, numbered lists, and images are detected automatically."} />
              <p className={HINT_CLASS}>Paste content here to auto pick blocks immediately, or edit the pasted text and click Generate Blocks.</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {["paragraph", "heading", "list", "link", "image"].map((t) => (
                <button key={t} type="button" className={BTN_ADD_BLOCK_CLASS}
                  onClick={() => addContentBlock(t)}>
                  + {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            {contentBlocks.map((block, index) => (
              <div key={index} className={ITEM_CLASS}>
                <div className={ITEM_HEADER_CLASS}>
                  <span className={TYPE_BADGE_CLASS}>#{index + 1} {block.type}</span>
                  <div className="flex gap-1.5 flex-wrap">
                    <button type="button" className={BTN_GHOST_CLASS}
                      onClick={() => moveBlock(index, -1)}>↑</button>
                    <button type="button" className={BTN_GHOST_CLASS}
                      onClick={() => moveBlock(index, 1)}>↓</button>
                    {contentBlocks.length > 1 && (
                      <button type="button" className={BTN_DANGER_CLASS}
                        style={{ padding: "6px 14px", fontSize: "12px" }}
                        onClick={() => removeContentBlock(index)}>Remove</button>
                    )}
                  </div>
                </div>

                {block.type === "paragraph" && (
                  <textarea rows={4} className={`${INPUT_CLASS} resize-y min-h-[90px]`} value={block.text}
                    onChange={(e) => updateContentBlock(index, { text: e.target.value })}
                    placeholder="Paragraph text…" />
                )}

                {block.type === "heading" && (
                  <div className="flex gap-2">
                    <div className="w-[90px] shrink-0">
                      <select className={INPUT_CLASS} value={block.level}
                        onChange={(e) => updateContentBlock(index, { level: Number(e.target.value) })}>
                        <option value={2}>H2</option>
                        <option value={3}>H3</option>
                        <option value={4}>H4</option>
                        <option value={5}>H5</option>
                        <option value={6}>H6</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <input className={INPUT_CLASS} value={block.text}
                        onChange={(e) => updateContentBlock(index, { text: e.target.value })}
                        placeholder="Heading text…" />
                    </div>
                  </div>
                )}

                {block.type === "list" && (
                  <div>
                    <div className="mb-3">
                      <select className={`${INPUT_CLASS} w-40`} value={block.style}
                        onChange={(e) => updateContentBlock(index, { style: e.target.value })}>
                        <option value="unordered">Unordered</option>
                        <option value="ordered">Ordered</option>
                      </select>
                    </div>
                    {block.items.map((item, ii) => (
                      <div key={ii} className="flex gap-2 mb-2 items-center">
                        <input className={INPUT_CLASS} value={item}
                          onChange={(e) => updateListItem(index, ii, e.target.value)}
                          placeholder={`Item ${ii + 1}`} />
                        <button type="button" className={BTN_REMOVE_INLINE_CLASS}
                          onClick={() => removeListItem(index, ii)}>✕</button>
                      </div>
                    ))}
                    <button type="button" className={`${BTN_TEXT_LINK_CLASS} mt-1`}
                      onClick={() => addListItem(index)}>+ Add item</button>
                  </div>
                )}

                {block.type === "link" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input className={INPUT_CLASS} value={block.text}
                      onChange={(e) => updateContentBlock(index, { text: e.target.value })}
                      placeholder="Link text" />
                    <input className={INPUT_CLASS} value={block.href}
                      onChange={(e) => updateContentBlock(index, { href: normalizeDetectedHref(e.target.value) || e.target.value })}
                      placeholder="https://example.com" />
                  </div>
                )}

                {block.type === "image" && (
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <label className="inline-flex items-center gap-2 bg-accent-red/8 border border-accent-red/20 text-accent-red text-sm font-semibold px-4.5 py-2 rounded-lg cursor-pointer transition-colors hover:bg-accent-red/15">
                        {uploadingBlockIndex === index ? "Uploading…" : "↑ Upload Image"}
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => handleContentBlockImageUpload(index, e)}
                          disabled={uploadingBlockIndex === index}
                          className="hidden" />
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input className={INPUT_CLASS} value={block.src}
                        onChange={(e) => updateContentBlock(index, { src: e.target.value })}
                        placeholder="/images/blog/example.jpg" />
                      <input className={INPUT_CLASS} value={block.alt}
                        onChange={(e) => updateContentBlock(index, { alt: e.target.value })}
                        placeholder="Alt text" />
                    </div>
                    {block.src && (
                      <img src={block.src} alt={block.alt || "Preview"}
                        className="mt-3.5 max-h-[140px] rounded-xl border border-border-light object-cover block" />
                    )}
                  </div>
                )}
              </div>
            ))}
              </details>
            </div>
          </div>

          {/* FAQ Items */}
          <div className={CARD_CLASS}>
            <h2 className={`${CARD_TITLE_CLASS} justify-between`}>
              <span className="flex items-center gap-2.5">
                <span className={CARD_TITLE_ICON_CLASS}>❓</span> FAQ Items
              </span>
              <button type="button" className={BTN_ADD_BLOCK_CLASS} onClick={addFaq}>+ Add FAQ</button>
            </h2>

            {faqs.map((faq, index) => (
              <div key={index} className={ITEM_CLASS}>
                <div className={ITEM_HEADER_CLASS}>
                  <span className="text-xs font-bold text-accent-red uppercase tracking-wide">FAQ #{index + 1}</span>
                  {faqs.length > 1 && (
                    <button type="button" className={BTN_DANGER_CLASS}
                      style={{ padding: "5px 14px", fontSize: "12px" }}
                      onClick={() => removeFaq(index)}>Remove</button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className={LABEL_CLASS}>Question</label>
                    <input className={INPUT_CLASS} value={faq.question}
                      onChange={(e) => updateFaq(index, "question", e.target.value)}
                      placeholder="What is…?" />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Answer</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <button type="button" className={BTN_GHOST_CLASS}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => wrapFaqAnswerSelection(index, "**", "**", "bold text")}>
                        Bold
                      </button>
                      <button type="button" className={BTN_GHOST_CLASS}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertFaqAnswerLink(index)}>
                        Link
                      </button>
                    </div>
                    <textarea rows={3} className={`${INPUT_CLASS} resize-y min-h-[90px]`} value={faq.answer}
                      ref={(el) => { faqAnswerRefs.current[index] = el; }}
                      onSelect={() => rememberFaqSelection(index)}
                      onBlur={() => rememberFaqSelection(index)}
                      onChange={(e) => updateFaq(index, "answer", e.target.value)}
                      placeholder="Answer text…" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Form actions */}
          <div className="flex items-center flex-wrap gap-3 pt-6 pb-2">
            <button type="submit" disabled={loading || loadingEdit} className={BTN_PRIMARY_CLASS}>
              {loading
                ? (editingSlug ? "Updating…" : "Creating…")
                : (editingSlug ? "Update Blog Post" : "Create Blog Post")}
            </button>
            {editingSlug && (
              <button type="button" disabled={loading} className={BTN_SECONDARY_CLASS}
                onClick={resetForm}>Cancel Edit</button>
            )}
            <a href={`/${routing.defaultLocale}/blog`} className={`${BTN_SECONDARY_CLASS} inline-block no-underline text-center`}>
              View Blog Listing
            </a>
          </div>

        </form>

        <hr className="border-t border-border-light my-7" />

        {/* Manage existing blogs */}
        <div className={CARD_CLASS}>
          <h2 className={CARD_TITLE_CLASS}>
            <span className={CARD_TITLE_ICON_CLASS}>📋</span> Manage Blogs
          </h2>
          <p className="text-[13px] text-muted mb-5 -mt-2">
            Edit or delete existing blog posts.
          </p>

          {loadingBlogs ? (
            <p className="text-muted text-sm">Loading blogs…</p>
          ) : blogList.length === 0 ? (
            <p className="text-muted text-sm">No blogs yet. Create one above.</p>
          ) : (
            <div className="border border-border-light rounded-xl overflow-hidden bg-bg-main">
              {blogList.map((blog) => (
                <div key={blog.slug} className="flex items-center justify-between flex-wrap gap-3 px-5 py-3.5 border-b border-border-light last:border-b-0 hover:bg-bg-soft">
                  <div>
                    <div className="font-semibold text-charcoal text-sm">{blog.title}</div>
                    <div className="text-[11px] text-muted mt-0.5">
                      /{routing.defaultLocale}/blog/{blog.slug}{!blog.hasFullContent && " · content missing"}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className={BTN_GHOST_CLASS}
                      disabled={loadingEdit || deletingSlug === blog.slug}
                      onClick={() => startEdit(blog.slug)}>Edit</button>
                    <button type="button" className={BTN_DANGER_CLASS}
                      style={{ padding: "6px 14px", fontSize: "12px" }}
                      disabled={deletingSlug === blog.slug || loadingEdit}
                      onClick={() => handleDelete(blog.slug)}>
                      {deletingSlug === blog.slug ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      </div>
      <Footer />
    </>
  );
}
